import { useEffect, useMemo, useState, type ReactNode } from "react";

export type ProcessDetailPanelData = {
  name: string | null;
  purpose: string | null;
  entity: string | null;
  department: string | null;
  status: string | null;
  applicability: string | null;
  reviewStatus: string | null;
  reviewOwner: string | null;
  reviewNotes: string | null;
  steps: string[];
  risks: string[];
  systems: string[];
  rawFields?: Record<string, unknown> | null;
};

type CardTone = "imported" | "added" | "ai";

type ReviewStatusOption = { value: string; label: string };

type ProcessDetailPanelProps = {
  process: ProcessDetailPanelData;
  fields: Array<{ key: string; label: string; enabled: boolean; order: number }>;
  canEditReview?: boolean;
  reviewStatusOptions?: ReviewStatusOption[];
  reviewOwnerOptions?: ReviewStatusOption[];
  onUpdateReview?: (payload: {
    reviewStatus?: string | null;
    reviewNotes?: string | null;
    reviewOwner?: string | null;
  }) => Promise<void>;
  canEditDetails?: boolean;
  onUpdateDetails?: (payload: {
    name?: string | null;
    entity?: string | null;
    department?: string | null;
    status?: string | null;
    purpose?: string | null;
    applicability?: string | null;
    steps?: string[] | null;
    risks?: string[] | null;
    systems?: string[] | null;
  }) => Promise<void>;
  canViewRaw?: boolean;
};

function toneClasses(tone: CardTone): string {
  switch (tone) {
    case "added":
      return "border-amber-200/70 bg-amber-50/60";
    case "ai":
      return "border-violet-200/70 bg-violet-50/60";
    default:
      return "border-sky-200/70 bg-sky-50/60";
  }
}

function DetailCard({
  label,
  value,
  tone = "imported",
  children,
}: {
  label: string;
  value?: string | null;
  tone?: CardTone;
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className={`rounded-lg border p-3 ${toneClasses(tone)}`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm text-foreground">{children ?? (value || "—")}</div>
    </div>
  );
}

function EditableCard({
  label,
  tone = "added",
  children,
}: {
  label: string;
  tone?: CardTone;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className={`rounded-lg border p-3 ${toneClasses(tone)}`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-2 text-sm text-foreground">{children}</div>
    </div>
  );
}

function formatValue(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === "boolean") return value ? "Oui" : "Non";
  if (Array.isArray(value)) {
    const entries = value
      .map((entry) => formatValue(entry))
      .filter((entry): entry is string => Boolean(entry));
    return entries.length ? entries.join(", ") : null;
  }
  if (typeof value === "object") {
    const serialized = JSON.stringify(value);
    return serialized && serialized !== "{}" ? serialized : null;
  }
  const text = String(value).trim();
  return text || null;
}

function compactList(values: string[] | null | undefined): string | null {
  if (!values?.length) return null;
  return values.join(", ");
}

function formatListInput(values: string[] | null | undefined): string {
  return values?.length ? values.join("\n") : "";
}

function parseListInput(value: string): string[] | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed
    .split(/\r?\n|,|;/g)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

type AttachmentEntry = { url: string; filename: string };

function extractAttachments(value: unknown): AttachmentEntry[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const record = entry as Record<string, unknown>;
      const urlValue = record.url ?? record.URL;
      if (typeof urlValue !== "string" || !urlValue.trim()) return null;
      const filenameValue = record.filename ?? record.name ?? record.title;
      const filename = typeof filenameValue === "string" && filenameValue.trim() ? filenameValue : "Attachment";
      return { url: urlValue, filename };
    })
    .filter(Boolean) as AttachmentEntry[];
}

function truncateUrl(url: string, maxLength = 48): string {
  if (url.length <= maxLength) return url;
  return `${url.slice(0, Math.max(0, maxLength - 10))}…${url.slice(-8)}`;
}

function renderAttachmentList(attachments: AttachmentEntry[]): JSX.Element {
  return (
    <ul className="space-y-1 text-sm">
      {attachments.map((attachment) => (
        <li key={`${attachment.url}-${attachment.filename}`} className="flex flex-col">
          <span className="font-medium">{attachment.filename}</span>
          <a
            href={attachment.url}
            className="text-xs text-blue-600 underline underline-offset-2"
            target="_blank"
            rel="noreferrer"
          >
            {truncateUrl(attachment.url)}
          </a>
        </li>
      ))}
    </ul>
  );
}

function buildRawEntries(rawFields: Record<string, unknown> | null | undefined) {
  if (!rawFields) return [];
  return Object.entries(rawFields).sort(([keyA], [keyB]) => keyA.localeCompare(keyB));
}

export function ProcessDetailPanel({
  process,
  fields,
  canEditReview = false,
  reviewStatusOptions = [],
  reviewOwnerOptions = [],
  onUpdateReview,
  canEditDetails = false,
  onUpdateDetails,
  canViewRaw = false,
}: ProcessDetailPanelProps): JSX.Element {
  const [detailInputs, setDetailInputs] = useState({
    name: process.name ?? "",
    entity: process.entity ?? "",
    department: process.department ?? "",
    status: process.status ?? "",
    purpose: process.purpose ?? "",
    applicability: process.applicability ?? "",
    steps: formatListInput(process.steps),
    systems: formatListInput(process.systems),
    risks: formatListInput(process.risks),
  });
  const [reviewStatusInput, setReviewStatusInput] = useState(process.reviewStatus ?? "");
  const [reviewOwnerInput, setReviewOwnerInput] = useState(process.reviewOwner ?? "");
  const [reviewNotesInput, setReviewNotesInput] = useState(process.reviewNotes ?? "");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setDetailInputs({
      name: process.name ?? "",
      entity: process.entity ?? "",
      department: process.department ?? "",
      status: process.status ?? "",
      purpose: process.purpose ?? "",
      applicability: process.applicability ?? "",
      steps: formatListInput(process.steps),
      systems: formatListInput(process.systems),
      risks: formatListInput(process.risks),
    });
    setReviewStatusInput(process.reviewStatus ?? "");
    setReviewOwnerInput(process.reviewOwner ?? "");
    setReviewNotesInput(process.reviewNotes ?? "");
  }, [process]);

  const orderedFields = useMemo(() => [...fields].sort((a, b) => a.order - b.order), [fields]);

  const reviewStatusLabel = useMemo(() => {
    const match = reviewStatusOptions.find((option) => option.value === reviewStatusInput);
    return match?.label ?? reviewStatusInput;
  }, [reviewStatusInput, reviewStatusOptions]);

  const handleReviewStatusChange = async (nextValue: string) => {
    setReviewStatusInput(nextValue);
    if (!onUpdateReview) return;
    setIsSaving(true);
    try {
      await onUpdateReview({ reviewStatus: nextValue || null });
      setSaveError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Impossible de mettre à jour le statut.";
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReviewOwnerChange = async (nextValue: string) => {
    setReviewOwnerInput(nextValue);
    if (!onUpdateReview) return;
    setIsSaving(true);
    try {
      await onUpdateReview({ reviewOwner: nextValue || null });
      setSaveError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Impossible de mettre à jour le responsable.";
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReviewNotesBlur = async () => {
    if (!onUpdateReview) return;
    const trimmed = reviewNotesInput.trim();
    if (trimmed === (process.reviewNotes ?? "")) return;
    setIsSaving(true);
    try {
      await onUpdateReview({ reviewNotes: trimmed || null });
      setSaveError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Impossible de mettre à jour les notes.";
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDetailsBlur = async (
    key: keyof typeof detailInputs,
    nextValue: string,
    baseline: string,
  ) => {
    if (!onUpdateDetails) return;
    if (nextValue.trim() === baseline.trim()) return;
    setIsSaving(true);
    try {
      const payload: Record<string, unknown> = {};
      if (key === "steps" || key === "risks" || key === "systems") {
        payload[key] = parseListInput(nextValue);
      } else {
        payload[key] = nextValue.trim() || null;
      }
      await onUpdateDetails(payload);
      setSaveError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Impossible de mettre à jour le processus.";
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const fieldDefinitions: Record<
    string,
    { label: string; value: string | null; tone: CardTone; editable?: boolean; render?: JSX.Element }
  > = {
    name: {
      label: "Name",
      value: formatValue(process.name),
      tone: "added",
      editable: canEditDetails,
      render: canEditDetails ? (
        <input
          className="h-9 w-full rounded border border-slate-200 px-2 text-sm"
          value={detailInputs.name}
          onChange={(event) => setDetailInputs((current) => ({ ...current, name: event.target.value }))}
          onBlur={() => void handleDetailsBlur("name", detailInputs.name, process.name ?? "")}
          disabled={isSaving}
        />
      ) : undefined,
    },
    entity: {
      label: "Entity",
      value: formatValue(process.entity),
      tone: "added",
      editable: canEditDetails,
      render: canEditDetails ? (
        <input
          className="h-9 w-full rounded border border-slate-200 px-2 text-sm"
          value={detailInputs.entity}
          onChange={(event) => setDetailInputs((current) => ({ ...current, entity: event.target.value }))}
          onBlur={() => void handleDetailsBlur("entity", detailInputs.entity, process.entity ?? "")}
          disabled={isSaving}
        />
      ) : undefined,
    },
    department: {
      label: "Department",
      value: formatValue(process.department),
      tone: "added",
      editable: canEditDetails,
      render: canEditDetails ? (
        <input
          className="h-9 w-full rounded border border-slate-200 px-2 text-sm"
          value={detailInputs.department}
          onChange={(event) => setDetailInputs((current) => ({ ...current, department: event.target.value }))}
          onBlur={() => void handleDetailsBlur("department", detailInputs.department, process.department ?? "")}
          disabled={isSaving}
        />
      ) : undefined,
    },
    status: {
      label: "Status",
      value: formatValue(process.status),
      tone: "added",
      editable: canEditDetails,
      render: canEditDetails ? (
        <input
          className="h-9 w-full rounded border border-slate-200 px-2 text-sm"
          value={detailInputs.status}
          onChange={(event) => setDetailInputs((current) => ({ ...current, status: event.target.value }))}
          onBlur={() => void handleDetailsBlur("status", detailInputs.status, process.status ?? "")}
          disabled={isSaving}
        />
      ) : undefined,
    },
    purpose: {
      label: "Purpose",
      value: formatValue(process.purpose),
      tone: "added",
      editable: canEditDetails,
      render: canEditDetails ? (
        <textarea
          className="min-h-[96px] w-full rounded border border-slate-200 px-2 py-1 text-sm"
          value={detailInputs.purpose}
          onChange={(event) => setDetailInputs((current) => ({ ...current, purpose: event.target.value }))}
          onBlur={() => void handleDetailsBlur("purpose", detailInputs.purpose, process.purpose ?? "")}
          disabled={isSaving}
        />
      ) : undefined,
    },
    applicability: {
      label: "Applicability",
      value: formatValue(process.applicability),
      tone: "added",
      editable: canEditDetails,
      render: canEditDetails ? (
        <input
          className="h-9 w-full rounded border border-slate-200 px-2 text-sm"
          value={detailInputs.applicability}
          onChange={(event) => setDetailInputs((current) => ({ ...current, applicability: event.target.value }))}
          onBlur={() => void handleDetailsBlur("applicability", detailInputs.applicability, process.applicability ?? "")}
          disabled={isSaving}
        />
      ) : undefined,
    },
    steps: {
      label: "Steps",
      value: compactList(process.steps),
      tone: "added",
      editable: canEditDetails,
      render: canEditDetails ? (
        <textarea
          className="min-h-[96px] w-full rounded border border-slate-200 px-2 py-1 text-sm"
          value={detailInputs.steps}
          onChange={(event) => setDetailInputs((current) => ({ ...current, steps: event.target.value }))}
          onBlur={() => void handleDetailsBlur("steps", detailInputs.steps, formatListInput(process.steps))}
          disabled={isSaving}
        />
      ) : undefined,
    },
    systems: {
      label: "Systems involved",
      value: compactList(process.systems),
      tone: "added",
      editable: canEditDetails,
      render: canEditDetails ? (
        <textarea
          className="min-h-[96px] w-full rounded border border-slate-200 px-2 py-1 text-sm"
          value={detailInputs.systems}
          onChange={(event) => setDetailInputs((current) => ({ ...current, systems: event.target.value }))}
          onBlur={() => void handleDetailsBlur("systems", detailInputs.systems, formatListInput(process.systems))}
          disabled={isSaving}
        />
      ) : undefined,
    },
    risks: {
      label: "Risks & exceptions",
      value: compactList(process.risks),
      tone: "added",
      editable: canEditDetails,
      render: canEditDetails ? (
        <textarea
          className="min-h-[96px] w-full rounded border border-slate-200 px-2 py-1 text-sm"
          value={detailInputs.risks}
          onChange={(event) => setDetailInputs((current) => ({ ...current, risks: event.target.value }))}
          onBlur={() => void handleDetailsBlur("risks", detailInputs.risks, formatListInput(process.risks))}
          disabled={isSaving}
        />
      ) : undefined,
    },
    reviewStatus: {
      label: "Review status",
      value: formatValue(reviewStatusLabel),
      tone: "added",
      editable: canEditReview,
      render: canEditReview ? (
        <select
          className="h-9 w-full rounded border border-slate-200 bg-white px-2 text-sm"
          value={reviewStatusInput}
          onChange={(event) => void handleReviewStatusChange(event.target.value)}
          disabled={isSaving}
        >
          <option value="">Sélectionner…</option>
          {reviewStatusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : undefined,
    },
    reviewOwner: {
      label: "Owner",
      value: formatValue(process.reviewOwner),
      tone: "added",
      editable: canEditReview,
      render: canEditReview ? (
        <select
          className="h-9 w-full rounded border border-slate-200 bg-white px-2 text-sm"
          value={reviewOwnerInput}
          onChange={(event) => void handleReviewOwnerChange(event.target.value)}
          disabled={isSaving}
        >
          <option value="">Sélectionner…</option>
          {reviewOwnerOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : undefined,
    },
    reviewNotes: {
      label: "Review notes",
      value: formatValue(process.reviewNotes),
      tone: "ai",
      editable: canEditReview,
      render: canEditReview ? (
        <textarea
          className="min-h-[96px] w-full rounded border border-slate-200 px-2 py-1 text-sm"
          value={reviewNotesInput}
          onChange={(event) => setReviewNotesInput(event.target.value)}
          onBlur={() => void handleReviewNotesBlur()}
          disabled={isSaving}
        />
      ) : undefined,
    },
  };

  const cards = orderedFields
    .filter((field) => field.enabled)
    .map((field) => {
      if (field.key.startsWith("raw:")) {
        const rawKey = field.key.slice(4);
        const rawValue = process.rawFields ? process.rawFields[rawKey] : null;
        const attachments = extractAttachments(rawValue);
        const value = formatValue(rawValue);
        const shouldRender = attachments.length > 0 || Boolean(value);
        if (!shouldRender) return null;
        return (
          <DetailCard key={field.key} label={field.label} tone="imported">
            {attachments.length ? renderAttachmentList(attachments) : value}
          </DetailCard>
        );
      }

      const definition = fieldDefinitions[field.key];
      if (!definition) return null;
      const isEditable = Boolean(definition.editable && definition.render);
      const hasValue = Boolean(definition.value && definition.value.trim());
      if (!isEditable && !hasValue) return null;
      if (isEditable && definition.render) {
        return (
          <EditableCard key={field.key} label={field.label} tone={definition.tone}>
            {definition.render}
          </EditableCard>
        );
      }
      return <DetailCard key={field.key} label={field.label} value={definition.value} tone={definition.tone} />;
    })
    .filter(Boolean);

  const rawEntries = useMemo(() => buildRawEntries(process.rawFields), [process.rawFields]);

  return (
    <div className="space-y-3">
      {cards.length ? (
        <div className="grid gap-3 md:grid-cols-2">{cards}</div>
      ) : (
        <div className="text-sm text-slate-500">Aucune donnée à afficher.</div>
      )}
      {saveError ? <div className="text-xs text-rose-600">{saveError}</div> : null}
      {canViewRaw && rawEntries.length ? (
        <details className="rounded-lg border border-border bg-muted/30 p-3">
          <summary className="cursor-pointer text-sm font-semibold text-foreground">Raw data</summary>
          <div className="mt-3 space-y-2">
            {rawEntries.map(([key, value]) => {
              const attachments = extractAttachments(value);
              const formatted = formatValue(value);
              if (!attachments.length && !formatted) return null;
              return (
                <div key={key} className="rounded-md border border-border/60 bg-white p-2 text-sm">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{key}</div>
                  <div className="mt-1 text-foreground">
                    {attachments.length ? renderAttachmentList(attachments) : formatted}
                  </div>
                </div>
              );
            })}
          </div>
        </details>
      ) : null}
    </div>
  );
}
